import { useMemo } from 'react';
import {
  ButtonLink,
  Flex,
  Header as BackstageHeader,
  type HeaderProps,
} from '@backstage/ui';
import { createQueryClientProvider } from '../QueryClientProvider';
import { SlackIcon } from '../SlackIcon';
import { useGetEntityByRef, usePluginMetadata } from '../../hooks';

const SLACK_CHANNEL_ANNOTATION = 'neo4j/slack-channel-id';
const SLACK_CHANNEL_URL = 'https://neo4j.slack.com/archives';

type SlackChannel = {
  id: string;
  teamName: string;
};

const PluginPageHeaderQueryClientProvider = createQueryClientProvider(
  undefined,
  { displayName: 'PluginPageHeaderQueryClientProvider' },
);

function slackChannelFromEntity(
  entity: {
    metadata: {
      name: string;
      title?: string;
      annotations?: Record<string, string>;
    };
    spec?: unknown;
  } | null,
): SlackChannel | undefined {
  const id = entity?.metadata.annotations?.[SLACK_CHANNEL_ANNOTATION];
  if (!entity || !id) {
    return undefined;
  }

  return {
    id,
    teamName:
      entity.spec &&
      typeof entity.spec === 'object' &&
      'profile' in entity.spec &&
      entity.spec.profile &&
      typeof entity.spec.profile === 'object' &&
      'displayName' in entity.spec.profile &&
      typeof entity.spec.profile.displayName === 'string'
        ? entity.spec.profile.displayName
        : (entity.metadata.title ?? entity.metadata.name),
  };
}

const PluginOwnerSlackAction = ({
  ownerEntityRef,
}: {
  ownerEntityRef: string;
}) => {
  const { data: entity } = useGetEntityByRef(ownerEntityRef);
  const channel = useMemo(
    () => slackChannelFromEntity(entity ?? null),
    [entity],
  );

  if (!channel) {
    return null;
  }

  return (
    <ButtonLink
      href={`${SLACK_CHANNEL_URL}/${channel.id}`}
      target="_blank"
      rel="noopener noreferrer"
      variant="secondary"
      iconStart={<SlackIcon />}
    >
      {channel.teamName}
    </ButtonLink>
  );
};

export type PluginPageHeaderProps = HeaderProps & {
  /** Overrides the owning team resolved from the plugin's catalog entity. */
  ownerEntityRefs?: string[];
};

export const PluginPageHeader = ({
  breadcrumbs,
  description,
  customActions,
  ownerEntityRefs,
  ...props
}: PluginPageHeaderProps) => {
  const pluginMetadata = usePluginMetadata();
  const ownerEntityRef = (ownerEntityRefs ??
    pluginMetadata?.ownerEntityRefs)?.[0];
  // Breadcrumbs mark a detail page, which must not show the plugin description.
  const defaultDescription = breadcrumbs?.length
    ? undefined
    : pluginMetadata?.description;
  const ownerAction = ownerEntityRef ? (
    <PluginPageHeaderQueryClientProvider>
      <PluginOwnerSlackAction ownerEntityRef={ownerEntityRef} />
    </PluginPageHeaderQueryClientProvider>
  ) : undefined;

  return (
    <BackstageHeader
      {...props}
      breadcrumbs={breadcrumbs}
      description={description ?? defaultDescription}
      customActions={
        (customActions !== undefined && customActions !== null) ||
        ownerAction ? (
          <Flex gap="2" align="center">
            {customActions}
            {ownerAction}
          </Flex>
        ) : undefined
      }
    />
  );
};
